import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { StatusCodes } from 'http-status-codes';
import { useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { displayError } from 'utils';
import { handleServiceError } from 'utils/axiosError';
import AlertModal from '../AlertModal';
import ConfirmModal from '../ConfirmModal';
import EditModeModalTitleBar from '../EditModeModalTitleBar';
import useGlobalModal from '../GlobalModal/useGlobalModal';
import imageUploadModalService from './imageUploadModalService';
import './style.scss';

export interface ImageUploadModalProps {
  imagepath?: string;
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  maxFileSize: number;
  onReflect?: (filename: string, imagepath: string) => void;
  onClose?: () => void;
}

function ImageUploadModal({
  imagepath = '',
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  maxFileSize,
  onReflect,
  onClose,
}: ImageUploadModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal, closeGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  //state
  const [currentImagePath, setCurrentImagePath] = useState(imagepath);

  // ref
  const uploadImageModalRef = useRef<any>(null);
  const confirmUploadModalRef = useRef<any>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);
  // const uploadedImageLinkRef = useRef<HTMLAnchorElement>(null);
  const uploadedImagePreviewRef = useRef<HTMLImageElement>(null);
  const dataUrlRef = useRef<string>('');

  function handleClose() {
    onClose && onClose();
    closeGlobalModal && closeGlobalModal();
  }

  function handleSelectFile() {
    if (inputFileRef.current) {
      inputFileRef.current.value = '';
      inputFileRef.current.click();
    }
  }

  function handleCloseConfirmModal() {
    uploadImageModalRef.current.dialog.classList.remove('d-none');
    confirmUploadModalRef.current.dialog.classList.add('d-none');
  }

  function handleFileChange() {
    if (inputFileRef.current) {
      // validate filetype
      if (inputFileRef.current.files) {
        const file = inputFileRef.current.files[0];
        const sizeInMB = file.size / (1024 * 1024);

        if (file) {
          var rFilter = /^(image\/jpeg|image\/png|image\/jpg|image\/webp|image\/gif)$/i;
          if (!rFilter.test(file.type)) {
            showGlobalModal(() => (
              <AlertModal title={t('App.warning')} message={t('ImageUploadModal.warn_upload_file_format')} />
            ));

            inputFileRef.current.value = '';
            return;
          }

          // validate size (width, height)
          var picReader = new FileReader();
          picReader.addEventListener('load', function (event) {
            var imageObj = new Image();
            imageObj.src = event.target?.result as string;

            imageObj.onerror = function () {
              showGlobalModal(() => (
                <AlertModal title={t('App.warning')} message={t('ImageUploadModal.warn_upload_file_format')} />
              ));
              if (inputFileRef.current) {
                inputFileRef.current.value = '';
              }
            };

            imageObj.onload = function () {
              if (imageObj.height < minHeight || imageObj.width < minWidth) {
                //TEST IMAGE MIN SIZE
                showGlobalModal(() => (
                  <AlertModal
                    title={t('App.warning')}
                    message={t('ImageUploadModal.warn_upload_min_size').replace(
                      '{0} x {1}',
                      `${minWidth} x ${minHeight}`
                    )}
                  />
                ));

                if (inputFileRef.current) {
                  inputFileRef.current.value = '';
                }

                return;
              } else if (imageObj.height > maxHeight || imageObj.width > maxWidth) {
                //TEST IMAGE MAX SIZE
                showGlobalModal(() => (
                  <AlertModal
                    title={t('App.warning')}
                    message={t('ImageUploadModal.warn_upload_max_size').replace(
                      '{0} x {1}',
                      `${maxWidth} x ${maxHeight}`
                    )}
                  />
                ));
                if (inputFileRef.current) {
                  inputFileRef.current.value = '';
                }
                return;
              } else if (sizeInMB > maxFileSize) {
                //TEST IMAGE MAX FILE SIZE
                showGlobalModal(() => (
                  <AlertModal
                    title={t('App.warning')}
                    message={t('ImageUploadModal.warn_upload_filesize').replace('{0}', `${maxFileSize}`)}
                  />
                ));
                if (inputFileRef.current) {
                  inputFileRef.current.value = '';
                }
                return;
              }

              // check image is valid
              if (uploadedImagePreviewRef.current) {
                const canvas = document.createElement('canvas');
                canvas.width = imageObj.width;
                canvas.height = imageObj.height;
                canvas.getContext('2d')?.drawImage(imageObj, 0, 0, imageObj.width, imageObj.height);
                var dataUrl = canvas.toDataURL('image/jpeg');
                dataUrlRef.current = dataUrl;
                uploadedImagePreviewRef.current.src = dataUrl;
                uploadImageModalRef.current.dialog.classList.add('d-none');
                confirmUploadModalRef.current.dialog.classList.remove('d-none');
              }
            };
          });

          //Read the image
          picReader.readAsDataURL(file);
        }
      }
    }
  }

  function handleReflect() {
    const bodyFormData = new FormData();

    if (inputFileRef.current && inputFileRef.current.files) {
      const file = inputFileRef.current.files[0];
      if (file) {
        bodyFormData.append('img_url', file, file.name);
        // bodyFormData.append('width', minWidth.toString());
        // bodyFormData.append('height', minHeight.toString());
      }
    }

    (async () => {
      try {
        const response = await imageUploadModalService.uploadImage(bodyFormData);

        const imagepath = response.data.url;
        const filename = response.data.filename;
        onReflect && onReflect(filename, imagepath);
        handleClose();
      } catch (e) {
        if (inputFileRef.current) inputFileRef.current.value = '';

        if ((e as AxiosError).response?.status === StatusCodes.UNAUTHORIZED) {
          handleClose();
        } else {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      }
    })();
  }

  function handleConfirmDelete() {
    if (currentImagePath.length > 0) {
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.warning')}
          message={t('ImageUploadModal.warn_confirm_delete')}
          onConfirm={handleDelete}
        />
      ));
    }
  }

  function handleDelete() {
    setCurrentImagePath('');
    onReflect && onReflect('', '');
  }

  function handlePreviewOriginSize() {
    var image = new Image();
    if (dataUrlRef.current.length > 0) {
      image.src = dataUrlRef.current;

      var windowTab = window.open('about:blank');
      setTimeout(function () {
        windowTab && windowTab.document.write(image.outerHTML);
      }, 300);
    }
  }

  return (
    <>
      <Modal
        ref={uploadImageModalRef}
        size="sm"
        dialogClassName="upload-image-modal"
        show
        backdrop="static"
        centered
        onHide={handleClose}
      >
        <EditModeModalTitleBar title={t('ImageUploadModal.title')} />

        <div className={isEditMode ? 'reedit-mode-wrapper' : undefined}>
          <Modal.Body>
            <section>
              <div className="py-2 px-3 bg-violet mb-1 fw-bold">{t('ImageUploadModal.current_image')}</div>
              <div className="preview-image text-center">
                {currentImagePath.replace(process.env.REACT_APP_IMAGE_URL || '', '').length > 0 ? (
                  <a href={currentImagePath} rel="noreferrer" target="_blank">
                    <img src={currentImagePath} style={{ maxHeight: '200px' }} alt="" />
                  </a>
                ) : (
                  <div className="no-image d-flex align-items-center justify-content-center">No Image</div>
                )}
              </div>
              <div className="my-1 d-flex align-items-center justify-content-between">
                <div>
                  <strong>
                    有効なサイズ: [{minWidth}x{minHeight} ~ {maxWidth}x{maxHeight}]
                  </strong>
                </div>
                <Button className="btn--red" onClick={handleConfirmDelete}>
                  {t('ImageUploadModal.delete')}
                </Button>
              </div>
            </section>
            <section>
              <div className="py-2 px-3 bg-violet fw-bold">{t('ImageUploadModal.re_upload')}</div>
              <div className="p-3 text-center">
                <Button variant="primary" onClick={handleSelectFile}>
                  {t('ImageUploadModal.file_selection')}
                </Button>
                <input
                  ref={inputFileRef}
                  type="file"
                  className="file-img-url"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </div>
            </section>
          </Modal.Body>

          <Modal.Footer className="justify-content-center">
            <Button variant="light" onClick={handleClose}>
              {t('ImageUploadModal.close')}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      <Modal
        ref={confirmUploadModalRef}
        size="sm"
        className="confirm-upload-modal d-none"
        show
        backdrop="static"
        centered
      >
        <Modal.Header className="bg-primary justify-content-center">
          <Modal.Title className="fs-6 text-white">{t('ImageUploadModal.upload_confirmation')}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="preview-image text-center" onClick={handlePreviewOriginSize}>
            <img ref={uploadedImagePreviewRef} alt="" />
          </div>
        </Modal.Body>

        <Modal.Footer className="justify-content-center">
          <Button variant="light" className="me-3" onClick={handleCloseConfirmModal}>
            {t('ImageUploadModal.close')}
          </Button>
          <Button variant="primary" onClick={handleReflect}>
            {t('ImageUploadModal.reflect')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ImageUploadModal;
